import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FinalReportAiEditMode, FinalReportAiEditResult } from '../api/assessmentsService';
import { applyDecisions, buildSegments, countHunks, DiffSegment } from '../utils/textDiff';

const SparkleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
    <path d="M5 2l.6 1.6L7.2 4.2l-1.6.6L5 6.4l-.6-1.6L2.8 4.2l1.6-.6z" />
  </svg>
);

export type AiEditTarget = 'selection' | 'full';

const wordCount = (text: string) => (text.trim().match(/\S+/g) ?? []).length;

/**
 * The AI tool button on an area card: a sparkle icon that drops down the edit
 * passes served by the API. Nothing runs until a pass is chosen.
 *
 * When the writer has highlighted part of the text, the menu offers to run the
 * pass on just that selection. Mouse presses inside the menu are prevented from
 * stealing focus so the textarea highlight stays visible while choosing.
 */
export const AiToolMenu: React.FC<{
  modes: FinalReportAiEditMode[];
  disabled?: boolean;
  busy?: boolean;
  /** Currently highlighted text in the editor, if any. */
  selectionText?: string;
  onSelect: (modeId: string, target: AiEditTarget) => void;
}> = ({ modes, disabled, busy, selectionText, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [useWholeText, setUseWholeText] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const hasSelection = Boolean(selectionText && selectionText.trim());
  const target: AiEditTarget = hasSelection && !useWholeText ? 'selection' : 'full';

  // A new selection resets the "whole text" override.
  useEffect(() => { setUseWholeText(false); }, [selectionText]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Keep focus (and the visible highlight) in the textarea while the menu is used.
  const keepEditorFocus = (event: React.MouseEvent) => event.preventDefault();

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={[
          'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition',
          open ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200 hover:text-primary-700',
          'disabled:cursor-not-allowed disabled:opacity-50',
        ].join(' ')}
        onMouseDown={keepEditorFocus}
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
        title="AI edit tools"
      >
        {busy
          ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
          : <SparkleIcon className="h-3.5 w-3.5" />}
        <span>AI tools</span>
        {hasSelection && !open && (
          <span className="rounded-full bg-primary-100 px-1.5 text-[10px] font-semibold text-primary-700">selection</span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
          onMouseDown={keepEditorFocus}
        >
          {hasSelection && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-primary-50/60 px-3 py-2 text-[11px]">
              <span className="text-gray-700">
                {target === 'selection'
                  ? <>Runs on your <span className="font-semibold">selection</span> ({wordCount(selectionText || '')} words)</>
                  : <>Runs on the <span className="font-semibold">whole text</span></>}
              </span>
              <button
                type="button"
                className="font-medium text-primary-700 underline underline-offset-2 hover:text-primary-800"
                onClick={() => setUseWholeText((prev) => !prev)}
              >
                {target === 'selection' ? 'Use whole text' : 'Use selection'}
              </button>
            </div>
          )}
          {modes.map((mode) => (
            <button
              key={mode.id}
              type="button"
              role="menuitem"
              className="block w-full px-3 py-2 text-left hover:bg-primary-50"
              onClick={() => {
                setOpen(false);
                onSelect(mode.id, target);
              }}
            >
              <div className="text-sm font-medium text-gray-900">{mode.label}</div>
              <div className="text-xs text-gray-500">{mode.description}</div>
            </button>
          ))}
          <div className="border-t border-gray-100 px-3 py-2 text-[11px] text-gray-500">
            Rewords only. Never adds ideas, ratings, or recommendations. You review every change.
            {!hasSelection && ' Highlight part of the text first to edit just that passage.'}
          </div>
        </div>
      )}
    </div>
  );
};

type Decision = 'pending' | 'accepted' | 'rejected';

const DecisionButton: React.FC<{
  kind: 'accept' | 'reject';
  onClick: () => void;
  title: string;
}> = ({ kind, onClick, title }) => (
  <button
    type="button"
    className={[
      'inline-flex h-5 w-5 items-center justify-center rounded border text-[11px] font-bold leading-none',
      kind === 'accept'
        ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
        : 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
    ].join(' ')}
    onClick={onClick}
    title={title}
    aria-label={title}
  >
    {kind === 'accept' ? '✓' : '✕'}
  </button>
);

const HunkView: React.FC<{
  removed: string;
  added: string;
  decision: Decision;
  onDecide: (decision: Decision) => void;
}> = ({ removed, added, decision, onDecide }) => {
  if (decision === 'accepted') {
    return (
      <span className="group/hunk inline rounded-sm bg-green-100 text-green-900 ring-1 ring-green-200">
        {added || <span className="text-green-700/70">∅</span>}
        <button
          type="button"
          className="ml-0.5 align-baseline text-[10px] text-green-700 underline decoration-dotted opacity-70 hover:opacity-100"
          onClick={() => onDecide('pending')}
          title="Undo — review this change again"
        >
          undo
        </button>
      </span>
    );
  }
  if (decision === 'rejected') {
    return (
      <span className="inline rounded-sm bg-gray-100 text-gray-800 ring-1 ring-gray-200">
        {removed}
        <button
          type="button"
          className="ml-0.5 align-baseline text-[10px] text-gray-600 underline decoration-dotted opacity-70 hover:opacity-100"
          onClick={() => onDecide('pending')}
          title="Undo — review this change again"
        >
          undo
        </button>
      </span>
    );
  }
  return (
    <span className="inline rounded-sm ring-1 ring-amber-200">
      {removed && (
        <del className="bg-red-50 text-red-800 line-through decoration-red-400">{removed}</del>
      )}
      {added && (
        <ins className="bg-green-50 text-green-900 no-underline">{added}</ins>
      )}
      <span className="mx-0.5 inline-flex items-center gap-0.5 align-baseline">
        <DecisionButton kind="accept" title="Accept this change" onClick={() => onDecide('accepted')} />
        <DecisionButton kind="reject" title="Keep the original here" onClick={() => onDecide('rejected')} />
      </span>
    </span>
  );
};

/**
 * Inline review of an AI suggestion: the writer's text with each proposed
 * change marked up, a yes/no on every change, and one Apply that writes the
 * result back into the editor. Nothing is saved until Apply.
 */
export const AiEditReview: React.FC<{
  original: string;
  result: FinalReportAiEditResult;
  modeLabel: string;
  onApply: (text: string) => void;
  onCancel: () => void;
}> = ({ original, result, modeLabel, onApply, onCancel }) => {
  const segments = useMemo<DiffSegment[]>(() => buildSegments(original, result.edited), [original, result.edited]);
  const total = useMemo(() => countHunks(segments), [segments]);
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});

  const decisionFor = (id: number): Decision => decisions[id] ?? 'pending';
  const acceptedCount = Object.values(decisions).filter((d) => d === 'accepted').length;
  const rejectedCount = Object.values(decisions).filter((d) => d === 'rejected').length;
  const pendingCount = total - acceptedCount - rejectedCount;

  // The suggestion is the default: anything the writer has not explicitly
  // rejected goes in on Apply, so they can save without touching every hunk.
  const idsToApply = useMemo(
    () => new Set(
      segments.flatMap((segment) => (
        segment.kind === 'hunk' && (decisions[segment.hunk.id] ?? 'pending') !== 'rejected'
          ? [segment.hunk.id]
          : []
      )),
    ),
    [segments, decisions],
  );
  const applyCount = idsToApply.size;

  const decideAll = (decision: Decision, onlyPending: boolean) => {
    setDecisions((prev) => {
      const next: Record<number, Decision> = { ...prev };
      for (const segment of segments) {
        if (segment.kind !== 'hunk') continue;
        if (onlyPending && (prev[segment.hunk.id] ?? 'pending') !== 'pending') continue;
        next[segment.hunk.id] = decision;
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(applyDecisions(segments, idsToApply));
  };

  return (
    <div className="space-y-3 rounded-md border border-primary-200 bg-primary-50/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <SparkleIcon className="h-4 w-4 text-primary-600" />
            {modeLabel}
            <span className="text-xs font-normal text-gray-500">
              {total === 0 ? 'no changes' : `${total} ${total === 1 ? 'change' : 'changes'}`}
              {result.model ? ` · ${result.model}` : ''}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            {result.scope === 'selection' ? 'Only your highlighted passage was edited. ' : ''}
            Reject any change you do not want; everything else goes in when you apply. Names were
            replaced before the text left the server.
          </p>
        </div>
        {total > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              className="font-medium text-green-700 hover:text-green-800 disabled:opacity-50"
              onClick={() => decideAll('accepted', true)}
              disabled={pendingCount === 0}
            >
              Accept remaining
            </button>
            <button
              type="button"
              className="font-medium text-red-700 hover:text-red-800 disabled:opacity-50"
              onClick={() => decideAll('rejected', true)}
              disabled={pendingCount === 0}
            >
              Reject remaining
            </button>
            <button
              type="button"
              className="text-gray-500 underline underline-offset-2 hover:text-gray-700 disabled:opacity-50"
              onClick={() => setDecisions({})}
              disabled={pendingCount === total}
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {total === 0 ? (
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
          The text already reads well for this pass — nothing to change.
        </div>
      ) : (
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-7 text-gray-900 whitespace-pre-wrap">
          {segments.map((segment, index) => (
            segment.kind === 'equal'
              ? <React.Fragment key={`eq-${index}`}>{segment.text}</React.Fragment>
              : (
                <HunkView
                  key={`hunk-${segment.hunk.id}`}
                  removed={segment.hunk.removed}
                  added={segment.hunk.added}
                  decision={decisionFor(segment.hunk.id)}
                  onDecide={(decision) => setDecisions((prev) => ({ ...prev, [segment.hunk.id]: decision }))}
                />
              )
          ))}
        </div>
      )}

      {result.changes.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">What the model says it did</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-gray-700">
            {result.changes.map((change, index) => (
              <li key={index}>{change}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-primary-100 pt-3">
        <span className="text-xs text-gray-600">
          {total === 0
            ? ''
            : [
              acceptedCount > 0 ? `${acceptedCount} accepted` : null,
              pendingCount > 0 ? `${pendingCount} undecided (applied unless rejected)` : null,
              rejectedCount > 0 ? `${rejectedCount} kept as written` : null,
            ].filter(Boolean).join(' · ')}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            onClick={onCancel}
          >
            {total === 0 ? 'Close' : 'Discard suggestion'}
          </button>
          {total > 0 && (
            <button
              type="button"
              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
              onClick={handleApply}
            >
              {applyCount === 0 ? 'Keep original' : `Apply ${applyCount} ${applyCount === 1 ? 'change' : 'changes'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
